import { useMemo, useCallback } from 'react';
import BoxAndWhiskerPlot, {
  type BoxPlotDataPoint,
  type BoxPlotSeries,
} from '@/components/plots/BoxAndWhiskerPlot';
import type { MapTableInsetRow } from '@/components/maps/insets';

interface UncertaintyTileProps {
  data: MapTableInsetRow[];
  yAxisTitle?: string;
  areaNameKey?: string;
}

export function UncertaintyTile({
  data,
  yAxisTitle = 'Customers',
  areaNameKey = 'rowName',
}: UncertaintyTileProps) {
  const visibleData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((row) => {
      const flatRow: Record<string, any> = { rowName: row.rowName };
      row.rowData?.forEach((varData) => {
        if (varData.variableId) flatRow[varData.variableId] = varData.variableValue;
        if (varData.variableSlug) flatRow[varData.variableSlug] = varData.variableValue;
      });
      return flatRow;
    });
  }, [data]);

  const boxPlotSeries = useMemo<BoxPlotSeries[]>(() => {
    if (!visibleData.length) return [];
    const dotSize = visibleData.length > 15 ? 7 : 11;
    const boxPlotDataPoints: BoxPlotDataPoint[] = visibleData.map((featureData) => {
      const name = featureData[areaNameKey] || featureData.name || 'Unknown';
      const getValue = (keys: string[]): number => {
        for (const key of keys) {
          const val = featureData[key];
          if (val !== undefined && val !== null && !isNaN(Number(val))) return parseFloat(val);
        }
        return 0;
      };
      let whiskerMin = getValue(['estimate10th', 'estimate_10th', 'estimate10']);
      let q1 = getValue(['estimate25th', 'estimate_25th', 'estimate25']);
      let median = getValue(['predicted_median', 'predictedMedian', 'median']);
      let q3 = getValue(['estimate75th', 'estimate_75th', 'estimate75']);
      let whiskerMax = getValue(['estimate90th', 'estimate_90th', 'estimate90']);
      const bestEstVal = getValue(['best_estimate', 'bestEstimate']);
      const bestEstimate = bestEstVal > 0 ? bestEstVal : null;

      if (whiskerMin === q1 && q1 === median && median === q3 && q3 === whiskerMax && whiskerMin !== 0) {
        const value = whiskerMin;
        const spread = Math.abs(value) * 0.1 || 0.1;
        whiskerMin = value - spread;
        q1 = value - spread * 0.5;
        median = value;
        q3 = value + spread * 0.5;
        whiskerMax = value + spread;
      }

      const dataPoint: BoxPlotDataPoint = {
        x: name.replace('Urbint ', ''),
        y: [whiskerMin, q1, median, q3, whiskerMax],
      };
      if (bestEstimate !== null) {
        dataPoint.goals = [{ value: bestEstimate, strokeWidth: 0, strokeHeight: dotSize, strokeLineCap: 'round', strokeColor: '#f97316' }];
      }
      return dataPoint;
    });
    return [{ name: 'Distribution', data: boxPlotDataPoints }];
  }, [visibleData, areaNameKey]);

  const tooltipFormatter = useCallback(({ seriesIndex, dataPointIndex, w }: { seriesIndex: number; dataPointIndex: number; w: any }) => {
    const chartData = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
    const areaName = chartData.x;
    const [min, q1Val, medianVal, q3Val, max] = chartData.y;
    const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });
    const bestEst = chartData.goals?.[0]?.value;
    return `<div style="padding:6px;border-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,.15);border:1px solid #e5e7eb;max-width:180px;font-size:10px;background:white;color:#374151">
      <div style="font-weight:bold;margin-bottom:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${areaName}</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:2px 8px">
        <span style="color:#6b7280">Best Est:</span><span style="font-weight:500;color:#c2410c">${bestEst !== undefined ? fmt(bestEst) : 'N/A'}</span>
        <span style="color:#6b7280">10th:</span><span style="font-weight:500">${fmt(min)}</span>
        <span style="color:#6b7280">25th:</span><span style="font-weight:500">${fmt(q1Val)}</span>
        <span style="color:#6b7280">50th:</span><span style="font-weight:500">${fmt(medianVal)}</span>
        <span style="color:#6b7280">75th:</span><span style="font-weight:500">${fmt(q3Val)}</span>
        <span style="color:#6b7280">90th:</span><span style="font-weight:500">${fmt(max)}</span>
      </div>
    </div>`;
  }, []);

  const boxPlotOptions = useMemo(() => {
    const dataPointCount = boxPlotSeries[0]?.data.length || 0;
    const isLargeDataset = dataPointCount > 15;
    const isVeryLargeDataset = dataPointCount > 30;
    const fontSize = isVeryLargeDataset ? '8px' : isLargeDataset ? '9px' : '11px';
    const rotateAngle = isVeryLargeDataset ? -90 : isLargeDataset ? -60 : -45;
    const maxHeight = isVeryLargeDataset ? 150 : isLargeDataset ? 120 : 100;
    return {
      chart: { id: 'uncertaintyBoxPlot', type: 'boxPlot' as const, height: '100%', width: '100%', background: 'transparent', animations: { enabled: false }, toolbar: { show: true, tools: { download: false, selection: false, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true }, autoSelected: 'zoom' as const }, zoom: { enabled: true, type: 'xy' as const }, redrawOnParentResize: true, redrawOnWindowResize: true },
      plotOptions: { boxPlot: { colors: { upper: '#60a5fa', lower: '#1e3a5f' } } },
      colors: ['#3b82f6'],
      stroke: { colors: ['#374151'], width: 1 },
      xaxis: { categories: boxPlotSeries[0]?.data.map((d: BoxPlotDataPoint) => d.x) || [], labels: { show: true, style: { fontSize, colors: '#374151' }, trim: true, hideOverlappingLabels: isLargeDataset, rotateAlways: true, rotate: rotateAngle, maxHeight }, tooltip: { enabled: false }, axisBorder: { show: true, color: '#9ca3af' }, axisTicks: { show: true, color: '#9ca3af' } },
      yaxis: { title: yAxisTitle ? { text: yAxisTitle, style: { fontSize: '12px', fontWeight: 600, color: '#374151' } } : undefined, labels: { style: { fontSize: '11px', colors: ['#374151'] }, formatter: (value: number) => Math.round(value).toLocaleString() }, axisBorder: { show: true, color: '#9ca3af' }, axisTicks: { show: true, color: '#9ca3af' } },
      grid: { borderColor: '#d1d5db', strokeDashArray: 4, xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
      tooltip: { enabled: true, x: { show: false }, custom: tooltipFormatter, style: { fontSize: '10px' } },
    };
  }, [boxPlotSeries, yAxisTitle, tooltipFormatter]);

  if (!visibleData.length) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-xs text-muted-foreground">No data available</p>
      </div>
    );
  }

  return (
    <div className="p-2 h-full flex flex-col" data-testid="uncertainty-chart">
      <BoxAndWhiskerPlot series={boxPlotSeries} options={boxPlotOptions} height="100%" width="100%" />
    </div>
  );
}
